import React, {useMemo} from 'react';
import {borderRadius, styled} from '@mui/system';
import {Image} from '@jobseeker/lotus/components/Image';
import {BgImage, DefaultJobLogo, RoundedStar, StarHirist} from '@jobseeker/lotus/icons';
import {Box, useMediaQuery} from '@mui/material';
import {addUtmParamsToUrl} from '@jobseeker/lotus/utils';
import {getRandomColor} from '@jobseeker/lotus/theme';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {useSearchParams} from 'next/navigation';
interface CompanyInfo {
  aggregateRatingOneDecimal: number;
  reviewsCount: number;
  url: string;
  companyType?: string;
  companyLogo?: string;
  companyName?: string;
}
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

interface CompanyHeaderProps {
  companyInfo: CompanyInfo;
  companyId: any;
  companyListingLogo?: string;
}

const CompanyHeaderWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'theme',
})(({theme}) => ({
  borderRadius: isAppIimjobs ? 12 : 0,
  boxShadow: '0px 6.48px 6px 2px rgba(41, 41, 41, 0.02);',
  backgroundColor: isAppIimjobs ? '#fff' : '#13355c',
  padding: isAppIimjobs ? '16px 16px 15px 16px' : '30px 50px',
  display: 'flex',
  marginTop: isAppIimjobs ? 0 : 32,
  backgroundImage: isAppIimjobs
    ? 'none'
    : `url(https://job-static.hirist.com/V8/static/media/background-bg.ba8365c7.webp)`,
  [theme.breakpoints.down('sm')]: {
    padding: isAppIimjobs ? '16px 16px 15px 16px' : '25px 15px',
  },
  alignItems:
    !isAppIimjobs && useMediaQuery('(max-width:900px)') ? 'center' : 'normal',
}));

const ImageCard = styled(Image)(({theme}) => ({
  width: 70,
  height: 70,
  borderRadius: 12,
  border: '0.6px solid rgba(126, 126, 126, 0.2)',
  objectFit: 'cover',
}));

const CompanyImageWrapper = styled(Box)(({theme}) => ({
  flexShrink: 0,
  width: isAppIimjobs ? 70 : 100,
  height: isAppIimjobs ? 70 : 100,
  marginRight: '12px',

  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: 12,
  },
  backgroundColor: '#fff',
  borderRadius: 12,
  [theme.breakpoints.down('sm')]: {
    width: isAppIimjobs ? 70 : 50,
    height: isAppIimjobs ? 70 : 50,
    borderRadius: isAppIimjobs ? 12 : 15,
    marginRight: '0px',
  },
  // border: '0.6px solid var(--Outlines-Dividers, rgba(126, 126, 126, 0.20))',
}));

const CompanyText = styled(Box)(({theme}) => ({
  marginRight: 8,
  fontSize: isAppIimjobs ? 16 : 24,
  marginTop: useMediaQuery('(min-width:900px)') ? '5px' : 0,
  fontWeight: isAppIimjobs ? '500' : 'bold',
  fontStyle: isAppIimjobs ? 'italic' : 'normal',

  color: isAppIimjobs ? '#333' : '#fff',
  [theme.breakpoints.down('sm')]: {
    fontSize: isAppIimjobs ? 16 : 20,
  },
}));

const CompanyHeaderRight = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginLeft: 12,
  flexDirection: useMediaQuery('(min-width:900px)') ? 'row' : 'column',
  alignItems: isAppIimjobs
    ? 'normal'
    : useMediaQuery('(max-width:900px)')
    ? 'normal'
    : 'center',
}));

const CompanyHeaderRightWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: isAppIimjobs ? 'normal' : 'center',
}));

const ImgStar = styled('i')(() => ({
  color: '#feae42',
  marginTop: 5,
  marginLeft: -4,
  fontSize: 16,
}));

const ABRatingDiv = styled(Box)(() => ({
  marginTop: !isAppIimjobs
    ? useMediaQuery('(min-width:900px)')
      ? '10px'
      : '0'
    : useMediaQuery('(min-width:900px)')
    ? '4px'
    : '-4px',
  marginLeft: useMediaQuery('(min-width:900px)') ? '6px' : '8px',
  fontFamily: isAppIimjobs ? 'Abhaya Libre' : 'Nunito Sans',
  fontSize: isAppIimjobs ? '18px' : '14px',
  color: isAppIimjobs ? '#333' : '#fff',

  fontWeight: isAppIimjobs ? 700 : 400,
  opacity: isAppIimjobs ? 1 : 0.7,
}));
const formatNumber = (num: number) => {
  if (num >= 1e6) {
    return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

const ABReviewsA = styled('a')(() => ({
  color: isAppIimjobs ? '#49966E' : '#fff',
  fontFamily: isAppIimjobs ? 'Poppins' : 'Nunito Sans',
  textDecoration: isAppIimjobs ? 'none' : 'default',
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 500,
  marginTop: !isAppIimjobs
    ? useMediaQuery('(min-width:900px)')
      ? '10px'
      : '0'
    : useMediaQuery('(min-width:900px)')
    ? 8
    : 0,
  marginLeft: useMediaQuery('(min-width:900px)') ? 8 : !isAppIimjobs ? 8 : 0,
}));

const CompanyTypeDiv = styled(Box)(() => ({
  fontSize: isAppIimjobs ? 14 : 16,
  fontWeight: isAppIimjobs ? 500 : 'bold',
  color: isAppIimjobs ? '#7E7E7E' : '#fff',
  marginLeft: '12px',
  marginTop: useMediaQuery('(min-width:900px)') && isAppIimjobs ? 8 : 0,
}));

const FlexContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: isAppIimjobs ? 'normal' : 'center',
}));

const CompanyHeader: React.FC<CompanyHeaderProps> = ({
  companyInfo,
  companyId,
  companyListingLogo,
}) => {
  const {
    aggregateRatingOneDecimal,
    reviewsCount,
    url,
    companyType,
    companyLogo = '',
    companyName = '',
  } = companyInfo;
  const randomColor = useMemo(() => getRandomColor(1), [companyListingLogo]);

  const searchParams = useSearchParams();

  const trackEvent = () => {
    TrackEventUBA('reviewsClick', 'click', {
      ref: searchParams.get('ref') || '',
      sessionUsername: sessionStorage.getItem('userId') || '',
      page: 'CompanyDetails',
      companyID: companyId ?? '',
      companyName: companyName ?? '',
    });
  };

  return (
    <CompanyHeaderWrapper data-testid="company-header">
      <CompanyImageWrapper>
        {companyListingLogo && companyListingLogo?.length > 0 ? (
          <ImageCard
            alt={companyName}
            src={companyListingLogo}
            loading="lazy"
          />
        ) : (
          <Box
            key={`${'color'}_${randomColor}`}
            className="joblist__logo"
            sx={{
              background: randomColor,
              p: 1.5,
              borderRadius: 3,
              height: 'min-content',
              lineHeight: 0,
            }}
          >
            <Image
              src={DefaultJobLogo}
              alt={'Logo'}
              width="70"
              height={'70'}
              objectFit="cover"
              quality={50}
            />
          </Box>
        )}
      </CompanyImageWrapper>
      <CompanyHeaderRightWrapper>
        <CompanyHeaderRight>
          {Boolean(companyName) && (
            <CompanyText>
              {isAppIimjobs ? companyName.toUpperCase() : companyName}
            </CompanyText>
          )}
          <FlexContainer>
            {aggregateRatingOneDecimal > 0 && (
              <Image
                height={isAppIimjobs ? 18 : 14}
                width={isAppIimjobs ? 18 : 14}
                src={isAppIimjobs ? RoundedStar : StarHirist}
                style={{
                  marginTop: useMediaQuery('(min-width:900px)') ? '8px' : '0px',
                  marginLeft: useMediaQuery('(min-width:900px)')
                    ? '0px'
                    : '0px',
                }}
                alt="star"
              />
            )}
            {aggregateRatingOneDecimal > 0 && (
              <ABRatingDiv>{aggregateRatingOneDecimal}</ABRatingDiv>
            )}

            {reviewsCount > 0 &&
              (useMediaQuery('(min-width:900px)') || isAppIimjobs == false) && (
                <ABReviewsA
                  title={`${companyName} Reviews`}
                  target="_blank"
                  rel="nofollow"
                  onClick={() => {
                    trackEvent();
                  }}
                  href={addUtmParamsToUrl(
                    url,
                    'RATING_REVIEWS',
                    useMediaQuery('(min-width:900px)')
                  )}
                  style={{alignSelf: 'flex-start', flexBasis: '100%'}}
                  data-testid="ab_review_count"
                >
                  {`(${formatNumber(reviewsCount)} Reviews & Ratings)`}
                </ABReviewsA>
              )}
          </FlexContainer>
          <FlexContainer>
            {reviewsCount > 0 &&
              !useMediaQuery('(min-width:900px)') &&
              isAppIimjobs && (
                <ABReviewsA
                  title={`${companyName} Reviews`}
                  target="_blank"
                  rel="nofollow"
                  href={addUtmParamsToUrl(
                    url,
                    'RATING_REVIEWS',
                    useMediaQuery('(min-width:900px)')
                  )}
                  style={{alignSelf: 'flex-start', flexBasis: '100%'}}
                >
                  {`(${formatNumber(reviewsCount)} Reviews & Ratings)`}
                </ABReviewsA>
              )}
          </FlexContainer>
        </CompanyHeaderRight>
        {Boolean(companyType) && (
          <CompanyTypeDiv>
            {isAppIimjobs ? companyType?.toUpperCase() : companyType}
          </CompanyTypeDiv>
        )}
      </CompanyHeaderRightWrapper>
    </CompanyHeaderWrapper>
  );
};

export default CompanyHeader;
